import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import http from 'helpers/requests';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import CgTable from 'modules/Common/table';
import CgCnGoodHoverText from 'modules/Common/entities/CgCnGoodHoverText';
import {
  useSupplierFactors,
  useSupplierFactorActions,
} from 'modules/FreeSupplier/useSupplierFactors';
import SupplierEmissionsModal from '../components/SupplierEmissionsModal';
import PropagationStatusBadge from '../components/PropagationStatusBadge';
import RevisionHistoryModal from '../components/RevisionHistoryModal';
import { cgToast } from 'modules/Common/ui/toast';
import { useMockData } from '../state/MockDataContext';
import { installationNameById } from '../state/adapters';
import { factorPeriodLabel, factorMethodsLabel, totalSee } from '../state/mockData';
import useCnGoodLabel from '../state/useCnGoodLabel';
import EmissionsDataFilters from '../components/EmissionsDataFilters';
import SefaSavingsCard from '../components/SefaSavingsCard';
import GapList from '../components/gaps/GapList';

const INITIAL_FILTERS = { year: [], cnCode: [], installationId: '', productId: '', source: '' };

const factorYear = (f) => (f.periodFrom ? f.periodFrom.slice(0, 4) : null);

// Emissions management for the free-supplier journey. The list is the VERSIONED
// supplier factor list (GET /supplier/factors → current published revisions),
// add/edit REUSE the real operator emissions modal (D48) re-pointed at
// /supplier/factors/publish|correct, and the destructive action is WITHDRAW
// (D23 — published factors are withdrawn, never hard-deleted).
export default function EmissionFactorsPage() {
  const { t } = useTranslation('freeSupplier');
  const { data, gaps, operator, installationDocs } = useMockData();
  // Loading/error/refetch state for the table chrome below — the row DATA
  // itself comes from `data.emissionFactors` (the single canonical read
  // model, B1), the SAME array MockDataContext derives for every other
  // consumer, not a locally re-mapped copy.
  const factorsQuery = useSupplierFactors();
  const { withdraw, invalidate } = useSupplierFactorActions();
  const cnGoodLabel = useCnGoodLabel();

  // The emissions modal target: { installation, selectedEmission } | null.
  const [emissionsModal, setEmissionsModal] = useState(null);
  // The revision-history target: historyAnchorRevisionId | null.
  const [historyAnchor, setHistoryAnchor] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  const docById = useMemo(
    () => new Map((installationDocs || []).map((d) => [String(d._id), d])),
    [installationDocs],
  );

  const emissionFactors = data.emissionFactors;

  const handleFiltersChange = (next) => {
    setFilters({ ...INITIAL_FILTERS, ...next });
    setPage(1);
  };

  const installationOptions = (installationDocs || []).map((d) => ({
    value: String(d._id),
    label: d.name,
  }));
  const installationName = (id) => installationNameById(installationDocs, id);

  // Item 16B: the CN Code filter's options — derived from the distinct
  // cnCodes actually present in the loaded factors (not the demo-era
  // hardcoded CN_CODE_OPTIONS, which offered 6 fixed steel/aluminium codes
  // regardless of what the supplier actually has data for).
  const cnCodeOptions = useMemo(() => {
    const distinctCodes = [...new Set(emissionFactors.map((f) => f.cnCode))];
    return distinctCodes.map((code) => ({ value: code, label: cnGoodLabel(code) }));
  }, [emissionFactors, cnGoodLabel]);

  const refresh = () => invalidate();

  // Add requires the supplier to pick the target installation explicitly (D52):
  // the emission factor's series identity includes its installation, so it must
  // never be silently defaulted. The shared modal's existing target-installation
  // picker (`installationChoice`, empty-on-open, blocks submit until chosen) is
  // wired here for both "save as draft" and "publish now". The `length > 0` guard
  // keeps the empty-state CTA (which also calls openAdd) from opening a dead-end
  // modal with no installation to pick.
  const openAdd = () => {
    if ((installationDocs || []).length === 0) return;
    // Mark an add — the installation list is read LIVE at render time (below),
    // never snapshotted here, so installations still streaming in via
    // useLiteInstallations appear in the picker instead of being frozen out.
    setEmissionsModal({ selectedEmission: null, requireInstallationChoice: true });
  };

  // Edit fetches the full EmissionsFactor record (same as the operator edit flow)
  // so the modal seeds from the real document, then attaches the current REVISION
  // id so the correct call addresses the right revision (D-2).
  //
  // A8: a failed seed fetch must NOT open the modal with IDs only — that would
  // let a correction be built from a blank/empty form and silently wipe every
  // field the supplier didn't re-enter. Surface a retryable notification and
  // keep the modal closed instead.
  const openEdit = async (row) => {
    const inst = docById.get(String(row.installationId)) || { _id: row.installationId };
    try {
      const res = await http.get(`/emissions/operator/${row.emissionsFactorId}`);
      const selectedEmission = { ...(res?.data || {}), currentRevisionId: row.currentRevisionId };
      // Q1 (S7 fix batch 3): the legacy record never carries a real
      // electricity breakdown for a supplier-published factor — see
      // `reconstructIndirectBreakdown` in InstallationRealEmissionsModal.jsx
      // buildDefaultsFrom, the counterpart that reconstructs a placeholder
      // breakdown so the indirect total survives a direct-only correction.
      // Surface why the electricity fields look pre-filled with round numbers.
      const hasRealIndirectBreakdown = Boolean(
        selectedEmission.indirectMeta?.electricityConsumed ||
        selectedEmission.indirectMeta?.indirectEmissionFactor,
      );
      const contextNotice =
        !hasRealIndirectBreakdown && Number(selectedEmission.emissionFactors?.indirect ?? 0) > 0
          ? t(
              'freeSupplier:emissionFactors.indirectBreakdownNotice',
              'Your original electricity breakdown wasn’t retained — the indirect total is preserved exactly as published. The fields below only need re-entry if you want to change it.',
            )
          : undefined;
      setEmissionsModal({ installation: inst, selectedEmission, contextNotice });
    } catch {
      cgToast.error(t('freeSupplier:emissionFactors.loadErrorTitle', 'Error'), {
        description: t(
          'freeSupplier:emissionFactors.loadErrorBody',
          "We couldn't load this factor's details — try again.",
        ),
      });
    }
  };

  const filtered = useMemo(() => {
    const needle = (filters.productId || '').trim().toLowerCase();
    return emissionFactors.filter((f) => {
      if (filters.year?.length && !filters.year.includes(factorYear(f))) return false;
      if (filters.cnCode?.length && !filters.cnCode.includes(f.cnCode)) return false;
      if (filters.installationId && f.installationId !== filters.installationId) return false;
      if (needle && !(f.productId || '').toLowerCase().includes(needle)) return false;
      if (filters.source && f.source !== filters.source) return false;
      return true;
    });
  }, [emissionFactors, filters]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (safePage - 1) * limit;
    return filtered.slice(start, start + limit);
  }, [filtered, safePage, limit]);

  // CgTable keys row actions off values[i]._id.
  const values = useMemo(() => paged.map((f) => ({ ...f, _id: f.id })), [paged]);

  const headers = [
    {
      label: t('freeSupplier:emissionFactors.headers.product', 'Product'),
      key: 'product',
      noCopyButton: true,
    },
    {
      label: t('freeSupplier:emissionFactors.headers.installation', 'Installation'),
      key: 'installation',
    },
    { label: t('freeSupplier:emissionFactors.headers.method', 'Production method'), key: 'method' },
    { label: t('freeSupplier:emissionFactors.headers.direct', 'Direct'), key: 'seeDirect' },
    { label: t('freeSupplier:emissionFactors.headers.indirect', 'Indirect'), key: 'seeIndirect' },
    { label: t('freeSupplier:emissionFactors.headers.totalSee', 'Total SEE'), key: 'total' },
    { label: t('freeSupplier:emissionFactors.headers.source', 'Source'), key: 'source' },
    { label: t('freeSupplier:emissionFactors.headers.sync', 'Sync'), key: 'sync' },
  ];

  const rows = values.map((f) => ({
    _id: f._id,
    product: (
      <div className="flex flex-col gap-0.5 min-w-[150px]">
        <CgCnGoodHoverText
          cnCode={f.cnCode}
          useAutonomousData
          variant="plain"
        />
        <span className="flex items-center justify-between gap-3 text-[10px] font-normal text-slate-400">
          <span title={factorPeriodLabel(f)}>{factorYear(f) || '—'}</span>
          <span className="text-slate-500">{f.productId || ''}</span>
        </span>
      </div>
    ),
    installation: (
      <span className="text-xs font-normal text-slate-600">
        {installationName(f.installationId)}
      </span>
    ),
    method: (
      <span className="text-xs font-normal whitespace-normal max-w-[180px] inline-block">
        {factorMethodsLabel(f)}
      </span>
    ),
    seeDirect: <span className="font-semibold text-slate-800">{f.seeDirect}</span>,
    seeIndirect: <span className="font-semibold text-slate-800">{f.seeIndirect}</span>,
    total: (
      <span className="font-bold text-primaryColor">
        {totalSee(f)} <span className="text-[10px] font-normal text-slate-400">tCO2e/t</span>
      </span>
    ),
    source:
      f.source === 'Communication template' ? (
        <span className="inline-flex items-center gap-1.5 text-[11px] font-normal text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5 whitespace-nowrap">
          <FontAwesomeIcon icon={faFileExcel} />
          {t('freeSupplier:emissionFactors.sourceTemplate', 'Template')}
        </span>
      ) : (
        <span className="text-[11px] font-normal text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
          {t('freeSupplier:emissionFactors.sourceManual', 'Manual')}
        </span>
      ),
    sync: <PropagationStatusBadge propagation={f.propagation} />,
  }));

  // Row actions: edit → correct, history → timeline, withdraw → the destructive
  // action (a confirm overlay; the in-use reason surfaces from the backend).
  const actions = [
    {
      icon: 'faPen',
      variant: 'none',
      label: t('freeSupplier:emissionFactors.actions.edit', 'Edit'),
      onClick: (row) => openEdit(row),
    },
    {
      icon: 'faClockRotateLeft',
      variant: 'none',
      label: t('freeSupplier:emissionFactors.actions.history', 'History'),
      onClick: (row) => setHistoryAnchor(row.historyAnchorRevisionId),
    },
    {
      icon: 'faBan',
      variant: 'danger',
      label: t('freeSupplier:emissionFactors.actions.withdraw', 'Withdraw'),
      // R4 (S7 fix batch 3): withdraw is non-idempotent (D23 — published
      // factors are withdrawn, never hard-deleted; a second call against an
      // already-withdrawn revision is a real error, not a no-op). Disable
      // this row's trigger the moment ITS revision is in flight — a `boolean`
      // action.disabled would block every OTHER row's Withdraw too, which
      // isn't the bug we're fixing. `withdraw.variables` is react-query's
      // last-submitted mutation argument, so this only lights up for the
      // exact row that's mid-withdraw.
      disabled: (row) => withdraw.isPending && withdraw.variables === row.currentRevisionId,
      overlay: {
        title: t('freeSupplier:emissionFactors.withdrawOverlay.title', 'Withdraw this factor'),
        body: t(
          'freeSupplier:emissionFactors.withdrawOverlay.body',
          'It stops being shared with the declarants you are connected to. A factor already referenced by a declarant’s submitted data cannot be withdrawn — publish a correction instead.',
        ),
        buttons: [
          {
            label: t('freeSupplier:emissionFactors.withdrawOverlay.confirm', 'Withdraw'),
            pendingLabel: t('freeSupplier:emissionFactors.withdrawOverlay.pending', 'Withdrawing…'),
            variant: 'danger',
            // mutateAsync (not mutate): the table's row-confirm dialog
            // (CgConfirmDialog via CgTable) awaits a thenable onClick to keep
            // the confirm open + locked until the request settles, instead of
            // closing immediately and leaving a window for a duplicate
            // withdraw of the same revision.
            onClick: (row) => withdraw.mutateAsync(row.currentRevisionId),
          },
        ],
      },
    },
  ];

  const isEmpty = emissionFactors.length === 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800 m-0">
            {t('freeSupplier:emissionFactors.title', 'Emissions Data')}
          </h1>
          <p className="text-sm text-slate-500 mt-1 m-0">
            {t(
              'freeSupplier:emissionFactors.subtitle',
              "Direct and indirect embedded emissions per good — the same fields your customers' communication templates ask for. Every change is versioned and re-shared automatically.",
            )}
          </p>
        </div>
        <CgRadixButton
          variant="primary"
          icon={faPlus}
          onClick={openAdd}
          disabled={(installationDocs || []).length === 0}
        >
          {t('freeSupplier:emissionFactors.addButton', 'Add emissions data')}
        </CgRadixButton>
      </div>

      {factorsQuery.isError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 flex items-center justify-between gap-4">
          <p className="text-sm text-red-700 m-0">
            {t(
              'freeSupplier:emissionFactors.loadListError',
              'We couldn’t load your emissions data.',
            )}
          </p>
          <CgRadixButton
            variant="outline"
            size="sm"
            onClick={() => factorsQuery.refetch()}
          >
            {t('freeSupplier:emissionFactors.tryAgain', 'Try again')}
          </CgRadixButton>
        </div>
      ) : null}

      <GapList
        gaps={gaps.filter(
          (g) =>
            g.scope.factorId ||
            ['stale_period', 'missing_emissions', 'missing_sefa', 'unverified'].includes(g.type),
        )}
        title={t('freeSupplier:emissionFactors.gapsTitle', 'Gaps in your emissions data')}
        collapsible
        defaultCollapsed
      />

      <CgTable
        flat
        loading={factorsQuery.isPending}
        tableKey="lite-emissions-data"
        headers={headers}
        data={rows}
        values={values}
        actions={actions}
        filter={
          <EmissionsDataFilters
            form={filters}
            onFormChange={handleFiltersChange}
            onReset={() => handleFiltersChange(INITIAL_FILTERS)}
            installationOptions={installationOptions}
            cnCodeOptions={cnCodeOptions}
          />
        }
        pagination={{
          page: safePage,
          limit,
          total,
          onPageChange: (newPage, newLimit) => {
            if (newLimit) {
              setPage(1);
              setLimit(newLimit);
            } else {
              setPage(newPage);
            }
          },
        }}
        emptyStateVariant="default"
        emptyStateTitle={
          isEmpty
            ? t('freeSupplier:emissionFactors.emptyState.title', 'No emissions data yet')
            : t(
                'freeSupplier:emissionFactors.emptyState.filteredTitle',
                'No records match these filters',
              )
        }
        emptyStateDescription={
          isEmpty
            ? t(
                'freeSupplier:emissionFactors.emptyState.description',
                'Add it manually or upload your communication template.',
              )
            : t(
                'freeSupplier:emissionFactors.emptyState.filteredDescription',
                'Try clearing or widening the filters above.',
              )
        }
        onEmptyStateCreate={isEmpty ? openAdd : undefined}
        emptyStateCreateLabel={t('freeSupplier:emissionFactors.addButton', 'Add emissions data')}
      />

      <SefaSavingsCard
        factor={emissionFactors[emissionFactors.length - 1]}
        dismissKey="sefa-savings-emissions"
      />

      {/* Real operator emissions modal — re-pointed at /supplier/factors. Add is
          per-installation; edit seeds from the fetched record + current revision id. */}
      {emissionsModal && (
        <SupplierEmissionsModal
          show
          onClose={() => setEmissionsModal(null)}
          operator={operator}
          installation={emissionsModal.installation}
          selectedEmission={emissionsModal.selectedEmission}
          installationChoice={
            emissionsModal.requireInstallationChoice
              ? { installations: installationDocs || [], required: true }
              : undefined
          }
          contextNotice={emissionsModal.contextNotice}
          handleRefresh={refresh}
        />
      )}

      <RevisionHistoryModal
        open={!!historyAnchor}
        onOpenChange={(o) => {
          if (!o) setHistoryAnchor(null);
        }}
        historyAnchorRevisionId={historyAnchor}
      />
    </div>
  );
}
